import * as styles from './FileManager.module.less';
import { useEffect, useState } from "react";
import { IContainer, IFolder, IFileRoot, IFile, IEntrySelection, IEntry } from "./model/fileModel";
import { ButtonVariants, Icon, Icons, SlimButton, TromanButton } from '../Buttons';
import { FileInputButton } from '../FileInputButton';
import _ from 'lodash';
import { FileManagerCrumbs } from './FileManagerCrumbs';
import { ImSet } from '../../tromanImmutable';
import { TromanChip, TromanColor } from '../TromanChip';
import { OwnerType } from '@/twapp/server/FileEndpoint';

export interface IFileManagerProps {
    root: IFileRoot;
    hasPublish: boolean;
    uploadFiles: (target: IContainer, files: FileList) => void;
    deleteFiles: (entries: IEntrySelection) => void;
    downloadFile: (file: IFile) => void;
    moveFiles: (entries: IEntrySelection) => void;
    editEntry: (container: IContainer, entry: IEntry) => void;
    createFolder: (container: IContainer) => void;
    updateMultiple: (entries: IEntrySelection) => void;
}

function remapPath(root: IFileRoot, folderPath: IFolder[]) {
    const target = folderPath.map(x => x.name.toLowerCase());
    const result: IFolder[] = [];
    let current: IContainer = root;
    for (let item of target) {
        const match = current.folders.find(x => x.name.toLowerCase() === item);
        if (!match) {
            break;
        }
        result.push(match);
        current = match;
    }
    return result;
}

function getSelectedEntries(container: IContainer, selection: ImSet<string>): IEntrySelection {
    return {
        container: container,
        folders: _.filter(container.folders, x => selection.has(x.id)),
        files: _.filter(container.files, x => selection.has(x.id)),
    }
}

function getAllIds(container: IContainer) {
    return container.files.map(x => x.id).concat(container.folders.map(x => x.id));
}

export const FileManager = (props: IFileManagerProps) => {
    const [current, setCurrent] = useState<IContainer>(props.root);
    const [path, setPath] = useState<IFolder[]>([]);
    const [selection, setSelection] = useState(ImSet<string>());

    useEffect(() => {
        if (props.root && props.root !== current) {
            const newPath = remapPath(props.root, path);
            const next = newPath.length === 0 ? props.root : newPath[newPath.length - 1];
            setPath(newPath);
            setCurrent(next);
            if (newPath.length === path.length) {
                setSelection(selection.intersect([...next.files, ...next.folders].map(x => x.id)));
            } else {
                setSelection(ImSet());
            }
        }
    }, [props.root]);

    const navTo = (path: IFolder[]) => {
        const next = path.length === 0 ? props.root : path[path.length - 1];
        setPath(path);
        setCurrent(next);
        setSelection(ImSet());
    }

    const itemCount = current.files.length + current.folders.length;

    return (
        <div className={styles.fileManager}>
            <section className='tab-section'>
            <div className={styles.tools}>
                <div>
                    <FileInputButton className={styles.toolButton} text='Ladda upp dokument' icon={Icons.Upload} onInput={x => props.uploadFiles(current, x)}/>
                    <FileInputButton className={styles.toolButton} text='Ladda upp mapp' folder={true} icon={Icons.Upload} onInput={x => props.uploadFiles(current, x)}/>
                    <TromanButton className={styles.toolButton} text='Skapa mapp' icon={Icons.Add} onClick={() => props.createFolder(current)}/>
                </div>
                <div>
                    {
                        props.hasPublish && (
                            <TromanButton className={styles.toolButton} text='Ändra publicering' disabled={selection.size === 0} onClick={() => props.updateMultiple(getSelectedEntries(current, selection))}/>
                        )
                    }
                    <TromanButton className={styles.toolButton} text='Flytta' disabled={selection.size === 0} onClick={() => props.moveFiles(getSelectedEntries(current, selection))}/>
                    <TromanButton className={styles.toolButton} text='Radera' icon={Icons.Delete} variant={ButtonVariants.Danger} disabled={selection.size === 0} onClick={() => props.deleteFiles(getSelectedEntries(current, selection))}/>
                </div>
            </div>
            </section>
            <section className='tab-section'>
                <FileManagerCrumbs path={path} setPath={navTo}/>
                <div className={styles.header}>
                    <div className={styles.checkHeader}>
                        <input type='checkbox'
                            checked={selection.size === itemCount}
                            onChange={() => setSelection(selection.size < itemCount ? ImSet(getAllIds(current)) : ImSet())}/>
                    </div>
                    <div className={styles.nameHeader}>Namn</div>
                    {
                        props.hasPublish && (
                            <>
                                <div className={styles.publishHeader}><Icon icon={Icons.Eye}/> Politiker</div>
                                <div className={styles.publishHeader}><Icon icon={Icons.Eye}/> Publik</div>
                            </>
                        )
                    }
                    <div className={styles.fillerHeader}></div>
                </div>
                <div>
                    {
                        current.folders.map(x => (
                            <div key={x.id} className={styles.folder} onDoubleClick={e => (e.target instanceof HTMLInputElement) || navTo([...path, x])}>
                                <div className={styles.check}><input type='checkbox' checked={selection.has(x.id)} onChange={e => setSelection(e.target.checked ? selection.add(x.id) : selection.remove(x.id))}/></div>
                                <div className={styles.icon}><Icon icon={Icons.Folder}/></div>
                                <div className={styles.name}>{x.name}</div>
                                {
                                    props.hasPublish && (
                                        <>
                                            <div className={styles.option}>
                                                {
                                                    x.totalPolitikerCount > 0 && (
                                                        <TromanChip text={x.totalPolitikerCount===x.totalFileCount ? 'Politiker' : `${x.totalPolitikerCount}/${x.totalFileCount}`} icon={Icons.Eye} color={TromanColor.Politiker}/>
                                                    )
                                                }
                                            </div>
                                            <div className={styles.option}>
                                                {
                                                    x.totalPublikCount > 0 && (
                                                        <TromanChip text={x.totalPublikCount===x.totalFileCount ? 'Publik' : `${x.totalPublikCount}/${x.totalFileCount}`} icon={Icons.Eye} color={TromanColor.Publik}/>
                                                    )
                                                }
                                            </div>
                                        </>
                                    )
                                }
                                <div className={styles.download}></div>
                                <div className={styles.edit}><SlimButton icon={Icons.Edit} onClick={() => props.editEntry(current, x)}/></div>
                            </div>
                        ))
                    }
                    {
                        current.files.map(x => (
                            <div key={x.id} className={styles.file}>
                                <div className={styles.check}><input type='checkbox' checked={selection.has(x.id)} onChange={e => setSelection(e.target.checked ? selection.add(x.id) : selection.remove(x.id))}/></div>
                                <div className={styles.icon}><Icon icon={Icons.File}/></div>
                                <div className={styles.name}>{x.name}</div>
                                {
                                    props.hasPublish && (
                                        <>
                                            <div className={styles.option}>{x.showPolitiker && <TromanChip text='Politiker' icon={Icons.Eye} color={TromanColor.Politiker}/>}</div>
                                            <div className={styles.option}>{x.showPublik && <TromanChip text='Publik' icon={Icons.Eye} color={TromanColor.Publik}/>}</div>
                                        </>
                                    )
                                }
                                <div className={styles.download}><SlimButton icon={Icons.Download} onClick={() => props.downloadFile(x)}/></div>
                                <div className={styles.edit}><SlimButton icon={Icons.Edit} onClick={() => props.editEntry(current, x)}/></div>
                            </div>
                        ))
                    }
                </div>
            </section>
        </div>
    );
}
